<script setup lang="ts">
/**
 * Aura Incorporation — Notice for Controller (Corporate) — FORM OF NOTICE MENTIONED IN SECTION 386AG(2)(a). 법인 컨트롤러 1곳당 1부(ctrlc_{id}/).
 *   템플릿(Notice for Controller (Corporate)) 키와 1:1 — 1(a) 개인 컨트롤러 칸은 원문도 빈칸(법인용 통지).
 *     · is_controller            : [[if is_controller]]☒ Yes / ☐ No*  → 1(b) 법인 컨트롤러 particulars
 *     · knows_other_controller   : [[if knows_other_controller]]     → 2(a)/2(b) 다른 컨트롤러 particulars
 *   No 면 해당 particulars 는 문서에서 비워진다(normalize). 발신 = 회사 비서(FLORENSIA LEOVANNY LIONG, 원문 고정),
 *   Acknowledgement 서명([[controller_signature]]) = 법인 컨트롤러 대표자(controller_representative_name).
 *   Client Acceptance 의 Corporate Controller 행 값이 이 스코프로 자동 복사된다(syncCafToStageDocs).
 */
import { computed, inject, onMounted } from 'vue';
import CddVarField from './cdd/CddVarField.vue';
import { useCddFill, DOC_SCOPE } from './cdd/useCddFill';
import './allotDoc.css';

const fill = useCddFill();
const scope = inject(DOC_SCOPE, '');
const K = (k: string) => scope + k;

const yes = (k: string) => String(fill.values[K(k)] ?? '').toLowerCase() === 'yes';
const isController = computed(() => yes('is_controller'));
const knowsOther = computed(() => yes('knows_other_controller'));
function setFlag(k: string, v: boolean) {
  fill.setValue(K(k), v ? 'yes' : 'no');
}
onMounted(() => {
  // 법인 컨트롤러에게 보내는 통지 — 기본은 1번 Yes, 2번 No
  if (!String(fill.values[K('is_controller')] ?? '').trim()) fill.setValue(K('is_controller'), 'yes');
  if (!String(fill.values[K('knows_other_controller')] ?? '').trim()) fill.setValue(K('knows_other_controller'), 'no');
});

const IND_BLANK = [
  'your full name:', 'your aliases, if any:', 'your residential address:', 'your email address:', 'your contact no.:',
  'your nationality:', 'your identity card number or passport number:', 'your date of birth:',
];
const CORP: Array<[string, string]> = [
  ['your name:', 'controller_entity_name'],
  ['your unique entity number issued by the Registrar, if any:', 'controller_entity_uen'],
  ['the address of your registered office:', 'controller_registered_office_address'],
  ['your email address:', 'controller_entity_email'],
  ['your contact no.:', 'controller_entity_contact_no'],
  ['your legal form:', 'controller_legal_form'],
  ['the jurisdiction where, and statute under which, you are formed or incorporated:', 'controller_jurisdiction'],
  ['the name of the corporate entity register of the jurisdiction in which you are formed or incorporated, if applicable:', 'controller_corporate_register_name'],
  ['the identification number or registration number on the corporate entity register of the jurisdiction where you are formed or incorporated, if applicable', 'controller_corporate_register_no'],
];
const OTHER_IND: Array<[string, string]> = [
  ['the person’s full name:', 'other_controller_full_name'],
  ['the person’s aliases, if any:', 'other_controller_alias'],
  ['the person’s residential address:', 'other_controller_residential_address'],
  ['the person’s email address:', 'other_controller_email'],
  ['the person’s contact number:', 'other_controller_contact_no'],
  ['the person’s nationality:', 'other_controller_nationality'],
  ['the person’s identity card number or passport number:', 'other_controller_identification_no'],
  ['the person’s date of birth:', 'other_controller_date_of_birth'],
];
const OTHER_CORP: Array<[string, string]> = [
  ['the person’s full name:', 'other_controller_entity_name'],
  ['the person’s unique entity number issued by the Registrar, if any:', 'other_controller_entity_uen'],
  ['the address of the person’s registered office:', 'other_controller_registered_office_address'],
  ['the person’s legal form:', 'other_controller_legal_form'],
  ['the person’s email address:', 'other_controller_entity_email'],
  ['the person’s contact number:', 'other_controller_entity_contact_no'],
  ['the jurisdiction where, and statute under which, the person is formed or incorporated:', 'other_controller_jurisdiction'],
  ['the name of the corporate entity register of the jurisdiction in which the person is formed or incorporated, if applicable:', 'other_controller_corporate_register_name'],
  ['the identification number or registration number on the corporate entity register of the jurisdiction where the person is formed or incorporated, if applicable:', 'other_controller_corporate_register_no'],
];
</script>

<template>
  <div class="allot-doc">
    <div class="doc-lbl-title" style="text-align:center">FORM OF NOTICE MENTIONED IN SECTION 386AG(2)(a) OF ACT</div>
    <p class="doc-p">Date of notice: <CddVarField field-key="notice_date" label="Date of Notice" /></p>
    <p class="doc-p">Dear <CddVarField field-key="corporate_controller_name" label="Corporate Controller Name" />,</p>
    <p class="doc-p">
      We know or have reasonable grounds to believe that you are a registrable controller of
      <CddVarField field-key="company_name" label="Company Name" />.
    </p>
    <p class="doc-p">
      This notice is sent under section 386AG(2)(a) of the Companies Act 1967 and requires you to provide the following
      information within 30 days after the date of this notice. Please send your reply to 1 Phillip Street, #08-00, Royal
      One Phillip, Singapore 048692. Failure to provide the information required by this notice may be an offence.
    </p>

    <!-- 1. -->
    <table class="cdd-tbl ctl-tbl">
      <tbody>
        <tr><td class="q-no">1.</td><td>Are you a registrable controller of <CddVarField field-key="company_name" label="Company Name" />?</td></tr>
        <tr>
          <td></td>
          <td>
            Your reply:
            <span class="cdd-cbx" @click="setFlag('is_controller', true)"><span class="box">{{ isController ? '✓' : '' }}</span> Yes</span>
            /
            <span class="cdd-cbx" @click="setFlag('is_controller', false)"><span class="box">{{ !isController ? '✓' : '' }}</span> No*</span>
          </td>
        </tr>
      </tbody>
    </table>
    <p class="doc-p note">
      *Delete as appropriate. If your reply is yes and you are an individual, please provide the particulars in
      sub-paragraph (a). If your reply is yes and you are a legal entity, please provide the particulars in sub-paragraph (b).
    </p>
    <table class="cdd-tbl ctl-tbl muted-tbl">
      <tbody>
        <tr><td class="q-no">a)</td><td colspan="2">If your reply is “yes” and you are an individual, please provide the following particulars:</td></tr>
        <tr v-for="l in IND_BLANK" :key="l"><td></td><td class="cdd-lcell">{{ l }}</td><td class="blank">(left blank — corporate controller notice)</td></tr>
        <tr><td></td><td class="cdd-lcell">the date on which you became an individual controller of <CddVarField field-key="company_name" label="Company Name" />:</td><td class="blank">—</td></tr>
      </tbody>
    </table>
    <table class="cdd-tbl ctl-tbl" :class="{ 'off-tbl': !isController }">
      <tbody>
        <tr><td class="q-no">b)</td><td colspan="2">If your reply is “yes” and you are a legal entity, please provide the following particulars:</td></tr>
        <tr v-for="[label, key] in CORP" :key="key">
          <td></td><td class="cdd-lcell">{{ label }}</td>
          <td><CddVarField :field-key="key" :label="label" /></td>
        </tr>
        <tr>
          <td></td><td class="cdd-lcell">the date on which you became a corporate controller of <CddVarField field-key="company_name" label="Company Name" />:</td>
          <td><CddVarField field-key="controller_date_became_corporate_controller" label="Date became corporate controller" /></td>
        </tr>
      </tbody>
    </table>

    <!-- 2. -->
    <table class="cdd-tbl ctl-tbl" style="margin-top:12px">
      <tbody>
        <tr>
          <td class="q-no">2.</td>
          <td>
            Do you know or have reasonable grounds to believe that any other person is a registrable controller of
            <CddVarField field-key="company_name" label="Company Name" /> or is likely to have that knowledge?
          </td>
        </tr>
        <tr>
          <td></td>
          <td>
            Your reply:
            <span class="cdd-cbx" @click="setFlag('knows_other_controller', true)"><span class="box">{{ knowsOther ? '✓' : '' }}</span> Yes</span>
            /
            <span class="cdd-cbx" @click="setFlag('knows_other_controller', false)"><span class="box">{{ !knowsOther ? '✓' : '' }}</span> No*</span>
          </td>
        </tr>
      </tbody>
    </table>
    <p class="doc-p note">
      *Delete as appropriate. If your reply is yes and the person is an individual, please provide the particulars in
      sub-paragraph (a). If your reply is yes and the person is a legal entity, please provide the particulars in sub-paragraph (b).
    </p>
    <div :class="{ 'off-tbl': !knowsOther }">
      <table class="cdd-tbl ctl-tbl">
        <tbody>
          <tr><td class="q-no">a)</td><td colspan="2">If your reply is “yes” and the person is an individual, please provide the following particulars to the best of your knowledge:</td></tr>
          <tr v-for="[label, key] in OTHER_IND" :key="key">
            <td></td><td class="cdd-lcell">{{ label }}</td>
            <td><CddVarField :field-key="key" :label="label" /></td>
          </tr>
          <tr>
            <td></td><td class="cdd-lcell">the date on which the person became an individual controller of <CddVarField field-key="company_name" label="Company Name" />:</td>
            <td><CddVarField field-key="other_controller_date_became_individual_controller" label="Date became individual controller" /></td>
          </tr>
        </tbody>
      </table>
      <table class="cdd-tbl ctl-tbl">
        <tbody>
          <tr><td class="q-no">b)</td><td colspan="2">If your reply is “yes” and the person is a legal entity, please provide the following particulars to the best of your knowledge:</td></tr>
          <tr v-for="[label, key] in OTHER_CORP" :key="key">
            <td></td><td class="cdd-lcell">{{ label }}</td>
            <td><CddVarField :field-key="key" :label="label" /></td>
          </tr>
          <tr>
            <td></td><td class="cdd-lcell">the date on which the person became a corporate controller of <CddVarField field-key="company_name" label="Company Name" />:</td>
            <td><CddVarField field-key="other_controller_date_became_corporate_controller" label="Date became corporate controller" /></td>
          </tr>
        </tbody>
      </table>
    </div>

    <p class="doc-p" style="margin-top:12px">In this notice —</p>
    <p class="doc-p ind">“controller”, “corporate controller”, “individual controller” and “legal entity” have the meanings given to them in section 386AB of the Companies Act 1967;</p>
    <p class="doc-p ind">“identity card” has the meaning given to it in section 2(1) of the National Registration Act 1965;</p>
    <p class="doc-p ind">“registrable” has the meaning given to it in section 386AC of the Companies Act 1967.</p>

    <p class="doc-p" style="margin-top:12px">Yours sincerely,</p>
    <div class="sig-line">_______________________________</div>
    <p class="doc-p" style="margin:2px 0 0"><b>FLORENSIA LEOVANNY LIONG</b><br />Company Secretary</p>

    <hr class="ack-hr" />
    <div class="doc-lbl-title" style="text-align:center">ACKNOWLEDGEMENT</div>
    <p class="doc-p">
      I, <CddVarField field-key="controller_representative_name" label="Representative Name (signs acknowledgement)" />,
      being the (Director/Beneficial Owner/Authorised Representative)* of
      <CddVarField field-key="corporate_controller_name" label="Corporate Controller Name" />, Company Registration No.
      <CddVarField field-key="corporate_controller_registration_no" label="Corporate Controller Registration No." />,
      hereby declare that the above information provided in response to this notice is true and accurate.
    </p>
    <p class="doc-p note">*Delete as appropriate.</p>
    <p class="doc-p">Signature: <span class="blank">(T-Sign — the representative above)</span></p>
    <p class="doc-p">Date: <CddVarField field-key="dated_on" label="Date" /></p>
  </div>
</template>

<style scoped>
.ctl-tbl { margin-top: 8px; }
.ctl-tbl .q-no { width: 32px; font-weight: 700; vertical-align: top; }
.ctl-tbl .cdd-lcell { width: 44%; }
.note { font-size: 11.5px; color: #555; font-style: italic; }
.ind { padding-left: 18px; }
.blank { color: #9aa3ad; font-style: italic; font-size: 11.5px; }
.muted-tbl, .off-tbl { opacity: 0.55; }
.sig-line { margin-top: 26px; }
.ack-hr { margin: 18px 0 10px; border: 0; border-top: 1px solid #999; }
</style>
